import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../App';
import { getTestimonials, createTestimonial, updateTestimonial, deleteTestimonial } from '../../api/testimonials';

const AdminTestimonials = () => {
  const { token } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    text: '',
    rating: 5,
    image_url: '',
    is_visible: true,
    order: 0
  });

  const fetchTestimonials = useCallback(async () => {
    try {
      const data = await getTestimonials();
      setTestimonials(data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      text: '',
      rating: 5,
      image_url: '',
      is_visible: true,
      order: testimonials.length + 1
    });
    setSelectedTestimonial(null);
  };

  const handleOpenModal = (testimonial = null) => {
    if (testimonial) {
      setSelectedTestimonial(testimonial);
      setFormData({
        name: testimonial.name,
        role: testimonial.role || '',
        text: testimonial.text,
        rating: testimonial.rating || 5,
        image_url: testimonial.image_url || '',
        is_visible: testimonial.is_visible,
        order: testimonial.order || 0
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedTestimonial) {
        await updateTestimonial(token, selectedTestimonial.id, formData);
      } else {
        await createTestimonial(token, formData);
      }
      fetchTestimonials();
      handleCloseModal();
      setMessage({ type: 'success', text: selectedTestimonial ? 'Opinia zaktualizowana' : 'Opinia dodana' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd podczas zapisywania' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę opinię?')) return;

    try {
      await deleteTestimonial(token, id);
      fetchTestimonials();
      setMessage({ type: 'success', text: 'Opinia usunięta' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd podczas usuwania' });
    }
  };

  const handleToggleVisibility = async (testimonial) => {
    try {
      await updateTestimonial(token, testimonial.id, { is_visible: !testimonial.is_visible });
      fetchTestimonials();
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd' });
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={i < rating ? 'text-yellow-400' : 'text-zinc-600'}>★</span>
    ));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Zarządzanie Opiniami</h1>
            <p className="text-zinc-400 mt-1">Dodaj i edytuj opinie klientów</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
            data-testid="add-testimonial-btn"
          >
            + Dodaj opinię
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
            {message.text}
          </div>
        )}

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className={`bg-zinc-800/50 border rounded-lg p-6 transition-all ${
                testimonial.is_visible ? 'border-zinc-700' : 'border-zinc-800 opacity-60'
              }`}
              data-testid={`testimonial-card-${testimonial.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {testimonial.image_url ? (
                    <img
                      src={testimonial.image_url}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-white">{testimonial.name}</h3>
                    {testimonial.role && (
                      <p className="text-sm text-zinc-400">{testimonial.role}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  {renderStars(testimonial.rating)}
                </div>
              </div>

              <p className="text-zinc-300 text-sm mb-4 line-clamp-4">"{testimonial.text}"</p>

              <div className="flex justify-between items-center pt-4 border-t border-zinc-700">
                <button
                  onClick={() => handleToggleVisibility(testimonial)}
                  className={`px-3 py-1 rounded text-sm ${
                    testimonial.is_visible 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-zinc-700 text-zinc-400'
                  }`}
                  data-testid={`toggle-testimonial-${testimonial.id}`}
                >
                  {testimonial.is_visible ? 'Widoczna' : 'Ukryta'}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenModal(testimonial)}
                    className="px-3 py-1 bg-zinc-700 text-white rounded text-sm hover:bg-zinc-600"
                    data-testid={`edit-testimonial-${testimonial.id}`}
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial.id)}
                    className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30"
                    data-testid={`delete-testimonial-${testimonial.id}`}
                  >
                    Usuń
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {testimonials.length === 0 && (
          <div className="text-center py-12 text-zinc-400">
            Brak opinii. Kliknij "Dodaj opinię" aby dodać pierwszą.
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-white mb-6">
                  {selectedTestimonial ? 'Edytuj opinię' : 'Dodaj opinię'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Imię i nazwisko *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      data-testid="testimonial-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Rola / Stanowisko</label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      placeholder="np. Specjalista IT, Przedsiębiorca"
                      className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      data-testid="testimonial-role-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Treść opinii *</label>
                    <textarea
                      value={formData.text}
                      onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                      required
                      rows={4}
                      className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      data-testid="testimonial-text-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Ocena</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFormData({ ...formData, rating: star })}
                          className={`text-2xl ${star <= formData.rating ? 'text-yellow-400' : 'text-zinc-600'}`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">URL zdjęcia</label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                      data-testid="testimonial-image-input"
                    />
                    {formData.image_url && (
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="mt-2 w-16 h-16 rounded-full object-cover"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Kolejność</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      min="0"
                      className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_visible"
                      checked={formData.is_visible}
                      onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
                    />
                    <label htmlFor="is_visible" className="text-zinc-300">Widoczna na stronie</label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600"
                    >
                      Anuluj
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                      data-testid="save-testimonial-btn"
                    >
                      {saving ? 'Zapisywanie...' : 'Zapisz'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTestimonials;
